import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Users, Briefcase, ChevronUp, ChevronDown, UserCog } from 'lucide-react';
import { AVP, Director, LoanOfficer, Loan } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
interface AvpDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  avp: AVP | null;
  directors: Director[];
}
type DirectorSortField = 'name' | 'loCount' | 'loansClosed' | 'loanVolume' | 'avgRevenue';
type LOSortField = 'name' | 'loansClosed' | 'loanVolume' | 'avgRevenue';
type LoanSortField = 'borrower' | 'product' | 'closeDate' | 'amount';
type SortDir = 'asc' | 'desc';
function SortHeader({
  label,
  field,
  activeField,
  direction,
  onSort,
  align = 'left'
}: {
  label: string;
  field: string;
  activeField: string;
  direction: SortDir;
  onSort: (field: any) => void;
  align?: 'left' | 'right';
}) {
  const isActive = field === activeField;
  return <button onClick={e => {
    e.stopPropagation();
    onSort(field);
  }} className={`group inline-flex items-center gap-1 text-xs uppercase ${isActive ? 'text-slate-200' : 'text-slate-500 hover:text-slate-300'} transition-colors ${align === 'right' ? 'justify-end w-full' : ''}`} data-id="element-19">
      <span data-id="element-20">{label}</span>
      {isActive ? direction === 'asc' ? <ChevronUp className="w-3 h-3 text-indigo-400" data-id="element-21" /> : <ChevronDown className="w-3 h-3 text-indigo-400" data-id="element-22" /> : <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" data-id="element-23" />}
    </button>;
}
function compare(a: any, b: any, dir: SortDir) {
  if (typeof a === 'string' && typeof b === 'string') {
    return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  }
  return dir === 'asc' ? a - b : b - a;
}
function LoansTable({
  loans
}: {
  loans: Loan[];
}) {
  const [sortField, setSortField] = useState<LoanSortField>('amount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const handleSort = (field: LoanSortField) => {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const sorted = useMemo(() => {
    return [...loans].sort((a, b) => compare(a[sortField], b[sortField], sortDir));
  }, [loans, sortField, sortDir]);
  return <div className="rounded-lg overflow-hidden bg-black/30 border border-white/5" data-id="element-24">
      <table className="w-full text-left" data-id="element-25">
        <thead data-id="element-26">
          <tr className="border-b border-white/5" data-id="element-27">
            <th className="py-2 px-3" data-id="element-28">
              <SortHeader label="Borrower" field="borrower" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-29" />
            </th>
            <th className="py-2 px-3" data-id="element-30">
              <SortHeader label="Product" field="product" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-31" />
            </th>
            <th className="py-2 px-3" data-id="element-32">
              <SortHeader label="Date" field="closeDate" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-33" />
            </th>
            <th className="py-2 px-3 text-right" data-id="element-34">
              <SortHeader label="Amount" field="amount" activeField={sortField} direction={sortDir} onSort={handleSort} align="right" data-id="element-35" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5" data-id="element-36">
          {sorted.map(loan => <tr key={loan.id} data-id="element-37">
              <td className="py-2 px-3 text-sm text-white" data-id="element-38">{loan.borrower}</td>
              <td className="py-2 px-3 text-xs text-slate-400" data-id="element-39">
                {loan.product}
              </td>
              <td className="py-2 px-3 text-xs text-slate-400" data-id="element-40">
                {loan.closeDate}
              </td>
              <td className="py-2 px-3 text-sm text-white text-right" data-id="element-41">
                {formatCurrency(loan.amount)}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
function LoanOfficerRow({
  lo
}: {
  lo: LoanOfficer & {
    avgRevenue: number;
  };
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className={`rounded-lg border overflow-hidden transition-colors ${expanded ? 'bg-white/[0.04] border-white/15' : 'bg-white/[0.02] border-white/5 hover:border-white/10'}`} data-id="element-42">
      <button onClick={() => setExpanded(!expanded)} className="w-full grid grid-cols-[20px_1fr_80px_120px_120px] gap-3 items-center px-4 py-2.5 text-left" data-id="element-43">
        <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-44" />
        <div className="flex items-center gap-3 min-w-0" data-id="element-45">
          <div className="w-7 h-7 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-200 text-[10px] font-semibold shrink-0" data-id="element-46">
            {lo.initials}
          </div>
          <div className="min-w-0" data-id="element-47">
            <p className="text-sm text-white font-medium truncate" data-id="element-48">{lo.name}</p>
            <p className="text-[10px] text-slate-500 truncate uppercase tracking-wider" data-id="element-49">
              Loan Officer
            </p>
          </div>
        </div>
        <span className="text-sm text-slate-300 text-right" data-id="element-50">
          {formatNumber(lo.loansClosed)}
        </span>
        <span className="text-sm text-white text-right" data-id="element-51">
          {formatCurrency(lo.loanVolume, true)}
        </span>
        <span className="text-sm text-emerald-400 text-right" data-id="element-52">
          {formatCurrency(lo.avgRevenue)}
        </span>
      </button>
      <AnimatePresence initial={false} data-id="element-53">
        {expanded && <motion.div key="content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-54">
            <div className="px-4 pb-4 pt-1 border-t border-white/5" data-id="element-55">
              <div className="flex items-center gap-2 text-xs uppercase text-slate-500 mb-2 mt-3" data-id="element-56">
                <Briefcase className="w-3.5 h-3.5" data-id="element-57" /> Contributing Loans
              </div>
              <LoansTable loans={lo.loans} data-id="element-58" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function DirectorBlock({
  director
}: {
  director: Director;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loSortField, setLoSortField] = useState<LOSortField>('avgRevenue');
  const [loSortDir, setLoSortDir] = useState<SortDir>('desc');
  const handleLoSort = (field: LOSortField) => {
    if (field === loSortField) {
      setLoSortDir(loSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setLoSortField(field);
      setLoSortDir('desc');
    }
  };
  const enrichedLOs = useMemo(() => director.loanOfficers.map(lo => ({
    ...lo,
    avgRevenue: lo.loansClosed > 0 ? lo.totalRevenue / lo.loansClosed : 0
  })), [director.loanOfficers]);
  const sortedLOs = useMemo(() => {
    return [...enrichedLOs].sort((a, b) => compare(a[loSortField], b[loSortField], loSortDir));
  }, [enrichedLOs, loSortField, loSortDir]);
  return <div className={`rounded-xl border overflow-hidden transition-colors ${expanded ? 'bg-indigo-500/[0.04] border-indigo-400/20' : 'bg-white/[0.02] border-white/10 hover:border-white/20'}`} data-id="element-59">
      <button onClick={() => setExpanded(!expanded)} className="w-full grid grid-cols-[24px_1fr_90px_120px_120px] gap-3 items-center px-4 py-3.5 text-left" data-id="element-60">
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-61" />
        <div className="flex items-center gap-3 min-w-0" data-id="element-62">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center text-indigo-200 text-sm font-bold shrink-0" data-id="element-63">
            {director.initials}
          </div>
          <div className="min-w-0" data-id="element-64">
            <p className="text-base text-white font-semibold truncate" data-id="element-65">
              {director.name}
            </p>
            <p className="text-[10px] text-indigo-300 truncate uppercase tracking-wider font-medium" data-id="element-66">
              Director
            </p>
          </div>
        </div>
        <div className="text-right" data-id="element-67">
          <p className="text-sm text-white font-medium" data-id="element-68">{director.loCount}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-69">
            LOs
          </p>
        </div>
        <div className="text-right" data-id="element-70">
          <p className="text-sm text-white font-medium" data-id="element-71">
            {formatCurrency(director.loanVolume, true)}
          </p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-72">
            Volume
          </p>
        </div>
        <div className="text-right" data-id="element-73">
          <p className="text-sm text-emerald-400 font-semibold" data-id="element-74">
            {formatCurrency(director.avgRevenue)}
          </p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-75">
            Avg Rev
          </p>
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-76">
        {expanded && <motion.div key="content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-77">
            <div className="px-4 pb-4 pt-1 border-t border-white/5" data-id="element-78">
              <div className="flex items-center gap-2 text-xs uppercase text-slate-500 mb-3 mt-3" data-id="element-79">
                <Users className="w-3.5 h-3.5" data-id="element-80" /> Loan Officers
              </div>

              {/* LO sortable header */}
              <div className="grid grid-cols-[20px_1fr_80px_120px_120px] gap-3 px-4 py-2 border-b border-white/5 mb-1" data-id="element-81">
                <span data-id="element-82"></span>
                <SortHeader label="Officer" field="name" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} data-id="element-83" />
                <SortHeader label="Loans" field="loansClosed" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-84" />
                <SortHeader label="Volume" field="loanVolume" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-85" />
                <SortHeader label="Avg Rev / Loan" field="avgRevenue" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-86" />
              </div>

              <div className="space-y-1.5" data-id="element-87">
                {sortedLOs.map(lo => <LoanOfficerRow key={lo.id} lo={lo} data-id="element-88" />)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function AvpDetailModal({
  isOpen,
  onClose,
  avp,
  directors
}: AvpDetailModalProps) {
  const [dirSortField, setDirSortField] = useState<DirectorSortField>('avgRevenue');
  const [dirSortDir, setDirSortDir] = useState<SortDir>('desc');
  useEffect(() => {
    // reset sort when avp changes
    setDirSortField('avgRevenue');
    setDirSortDir('desc');
  }, [avp?.id]);
  const sortedDirectors = useMemo(() => {
    return [...directors].sort((a, b) => compare(a[dirSortField], b[dirSortField], dirSortDir));
  }, [directors, dirSortField, dirSortDir]);
  if (!avp) return null;
  const handleDirSort = (field: DirectorSortField) => {
    if (field === dirSortField) {
      setDirSortDir(dirSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setDirSortField(field);
      setDirSortDir('desc');
    }
  };
  return <AnimatePresence data-id="element-89">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-90" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-91">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-6 max-w-6xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-92">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-93">
                <div className="flex items-center gap-4" data-id="element-94">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-200 font-bold text-xl" data-id="element-95">
                    {avp.initials}
                  </div>
                  <div data-id="element-96">
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-[10px] uppercase tracking-wider font-medium mb-1.5" data-id="element-97">
                      <UserCog className="w-3 h-3" data-id="element-98" /> AVP
                    </div>
                    <h2 className="text-2xl font-bold text-white" data-id="element-99">
                      {avp.name}
                    </h2>
                    <p className="text-sm text-slate-400" data-id="element-100">
                      {avp.division} Division · Rank #{avp.rank}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-101">
                  <X className="w-5 h-5" data-id="element-102" />
                </button>
              </div>

              {/* Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-6 border-b border-white/5" data-id="element-103">
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-104">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-105">Directors</p>
                  <p className="text-lg font-bold text-white" data-id="element-106">
                    {directors.length}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-107">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-108">Loan Officers</p>
                  <p className="text-lg font-bold text-white" data-id="element-109">{avp.loCount}</p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-110">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-111">Loans Closed</p>
                  <p className="text-lg font-bold text-white" data-id="element-112">
                    {formatNumber(avp.loansClosed)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-113">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-114">Loan Volume</p>
                  <p className="text-lg font-bold text-white" data-id="element-115">
                    {formatCurrency(avp.loanVolume, true)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-116">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-117">Avg Revenue</p>
                  <p className="text-lg font-bold text-emerald-400" data-id="element-118">
                    {formatCurrency(avp.avgRevenue)}
                  </p>
                </div>
              </div>

              {/* Director hierarchy */}
              <div className="p-6 max-h-[60vh] overflow-y-auto" data-id="element-119">
                <div className="flex items-center justify-between mb-4" data-id="element-120">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-121">
                    <UserCog className="w-4 h-4 text-indigo-400" data-id="element-122" /> Director
                    Hierarchy
                  </h3>
                  <span className="text-xs text-slate-500" data-id="element-123">
                    Click a director to view their loan officers
                  </span>
                </div>

                {/* Director sortable header */}
                <div className="grid grid-cols-[24px_1fr_90px_120px_120px] gap-3 px-4 py-2 border-b border-white/5 mb-3" data-id="element-124">
                  <span data-id="element-125"></span>
                  <SortHeader label="Director" field="name" activeField={dirSortField} direction={dirSortDir} onSort={handleDirSort} data-id="element-126" />
                  <SortHeader label="LOs" field="loCount" activeField={dirSortField} direction={dirSortDir} onSort={handleDirSort} align="right" data-id="element-127" />
                  <SortHeader label="Volume" field="loanVolume" activeField={dirSortField} direction={dirSortDir} onSort={handleDirSort} align="right" data-id="element-128" />
                  <SortHeader label="Avg Rev / Loan" field="avgRevenue" activeField={dirSortField} direction={dirSortDir} onSort={handleDirSort} align="right" data-id="element-129" />
                </div>

                <div className="space-y-2" data-id="element-130">
                  {sortedDirectors.map(d => <DirectorBlock key={d.id} director={d} data-id="element-131" />)}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/5 flex items-center justify-end bg-black/10" data-id="element-132">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/10" data-id="element-133">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}